import { useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../lang";
import { company, lines, products, projects } from "../data";
import { ProductCard } from "./productpage";

const byId = Object.fromEntries(products.map((p) => [p.id, p]));

function LinePicker() {
  const { t } = useLang();
  const [line, setLine] = useState(lines[0].id);
  const picks = lines.find((l) => l.id === line).picks.map((id) => byId[id]);
  return (
    <section className="wrap py-16 md:py-20" aria-labelledby="picker-title">
      <h2 id="picker-title" className="text-3xl font-bold md:text-4xl">{t("ไลน์ของคุณผลิตอะไร?", "What does your line make?")}</h2>
      <p className="mt-3 max-w-2xl text-lg text-ink-soft">
        {t("เลือกประเภทสินค้า แล้วดูระบบที่เรามักติดตั้งให้ไลน์แบบนั้น", "Pick your product type to see the systems we usually build for that kind of line.")}
      </p>
      <div className="mt-6 flex flex-wrap gap-2" role="group" aria-label={t("ประเภทสินค้า", "Product type")}>
        {lines.map((l) => (
          <button
            key={l.id}
            onClick={() => setLine(l.id)}
            aria-pressed={line === l.id}
            className={`rounded-md border px-4 py-2.5 font-medium transition-colors ${
              line === l.id ? "border-ink bg-ink text-white" : "border-steel-200 bg-white hover:border-ink"
            }`}
          >
            {t(l.name)}
          </button>
        ))}
      </div>
      <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" aria-live="polite">
        {picks.map((p) => <ProductCard key={p.id} p={p} />)}
      </div>
      <Link to="/products" className="mt-8 inline-block font-semibold text-gws-blue underline-offset-4 hover:underline">
        {t("ดูผลิตภัณฑ์ทั้งหมด →", "See all products →")}
      </Link>
    </section>
  );
}

const steps = [
  {
    img: "/images/production-line.webp",
    title: { th: "ออกแบบตามไลน์จริง", en: "Designed around your line" },
    text: { th: "เข้าดูหน้างาน วัดพื้นที่ จังหวะการผลิต และขนาดสินค้า แล้วออกแบบเครื่องให้พอดีกับไลน์เดิม", en: "We survey the site, the space, line rate and product size, then design the machine to fit the line you already run." },
  },
  {
    img: "/images/cleated-conveyor.webp",
    title: { th: "ผลิตด้วยสแตนเลสเกรดอาหาร", en: "Built in food-grade stainless" },
    text: { th: "โครงสร้างสแตนเลส ล้างทำความสะอาดง่าย เลือกสายพาน PU, modular หรือ wire-mesh ตามชนิดอาหาร", en: "Stainless frames that wash down easily, with PU, modular or wire-mesh belts chosen for the food you run." },
  },
  {
    img: "/images/control-cabinet.webp",
    title: { th: "ติดตั้ง เดินไฟ และระบบควบคุม", en: "Installed, wired and controlled" },
    text: { th: "ทีมเดียวทำทั้งตู้คอนโทรล PLC งานไฟฟ้า และติดตั้งหน้างาน จนเครื่องเดินได้จริง", en: "One team handles the control cabinet, PLC, wiring and on-site install until the line actually runs." },
  },
];

function Homepage() {
  const { t } = useLang();
  return (
    <>
      <section className="relative isolate overflow-hidden bg-ink text-white">
        <img src="/images/mesh-conveyor.webp" alt={t("สายพาน wire-mesh สแตนเลสที่ GWS ติดตั้งในโรงงานอาหาร", "Stainless wire-mesh conveyor GWS installed in a food plant")} className="absolute inset-0 -z-10 h-full w-full object-cover" fetchpriority="high" />
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-ink via-ink/80 to-ink/20" />
        <div className="wrap flex min-h-[78vh] flex-col justify-end py-14 md:min-h-[70vh] md:py-20">
          <p className="font-semibold text-safety">{t("System Integrator • เครื่องจักรสั่งทำ", "System integrator • Custom machinery")}</p>
          <h1 className="mt-3 max-w-3xl text-4xl font-bold leading-tight sm:text-6xl lg:text-7xl">
            {t("ระบบอัตโนมัติสำหรับโรงงานอาหาร", "Automation built for food factories")}
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-steel-100 md:text-xl">
            {t(
              "ออกแบบ ผลิต และติดตั้งสายพานลำเลียง เครื่องป้อน ระบบชั่ง และหุ่นยนต์ปลายไลน์ ให้ไลน์ผลิตอาหารของคุณเดินเร็วขึ้นและใช้คนน้อยลง",
              "We design, build and install conveyors, feeders, weighing systems and end-of-line robots so your food line runs faster with fewer hands."
            )}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href={`tel:${company.tel}`} className="btn-primary">{t("โทร", "Call")} {company.phone}</a>
            <Link to="/products" className="btn-ghost">{t("ดูผลิตภัณฑ์", "View products")}</Link>
          </div>
        </div>
      </section>

      <section className="border-b border-steel-200 bg-white">
        <dl className="wrap grid grid-cols-2 gap-6 py-8 text-sm md:grid-cols-4">
          {[
            [t("ลูกค้า", "Clients"), t("โรงงานอาหาร เครื่องดื่ม และขนม", "Food, beverage & snack plants")],
            [t("งานหลัก", "Core work"), t("สายพานและเครื่องสั่งทำ", "Conveyors & custom machines")],
            [t("หุ่นยนต์", "Robots"), t("ตัวแทนจำหน่าย ABB", "Authorised ABB dealer")],
            [t("ที่ตั้ง", "Based in"), t("สมุทรปราการ", "Samut Prakan")],
          ].map(([k, v]) => (
            <div key={k}>
              <dt className="text-steel-600">{k}</dt>
              <dd className="mt-1 font-semibold">{v}</dd>
            </div>
          ))}
        </dl>
      </section>

      <LinePicker />

      <section className="bg-white py-16 md:py-20">
        <div className="wrap">
          <h2 className="text-3xl font-bold md:text-4xl">{t("จบในทีมเดียว ตั้งแต่แบบจนเครื่องเดิน", "One team, from drawing to running line")}</h2>
          <div className="mt-10 grid gap-10 md:grid-cols-3">
            {steps.map((s) => (
              <div key={s.img}>
                <img src={s.img} alt="" loading="lazy" className="aspect-[4/3] w-full rounded-lg object-cover" />
                <h3 className="mt-4 text-xl font-semibold">{t(s.title)}</h3>
                <p className="mt-2 text-ink-soft">{t(s.text)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="wrap py-16 md:py-20" aria-labelledby="work-title">
        <h2 id="work-title" className="text-3xl font-bold md:text-4xl">{t("ผลงานที่ส่งมอบแล้ว", "Delivered projects")}</h2>
        <ul className="mt-8 divide-y divide-steel-200 border-y border-steel-200">
          {projects.map((p) => (
            <li key={t(p.name)} className="grid gap-1 py-4 sm:grid-cols-[1fr_auto] sm:items-baseline sm:gap-6">
              <div>
                <p className="text-lg font-semibold">
                  {t(p.name)}
                  {p.food && <span className="ml-2 rounded bg-safety px-1.5 py-0.5 align-middle text-xs font-semibold text-ink">{t("อาหาร", "Food")}</span>}
                </p>
                <p className="text-ink-soft">{t(p.client)}</p>
              </div>
              <p className="tabular-nums text-steel-600">{p.year}</p>
            </li>
          ))}
        </ul>
      </section>

      <section className="bg-ink text-white">
        <div className="wrap grid gap-8 py-14 md:grid-cols-[3fr_2fr] md:items-center">
          <div>
            <h2 className="text-3xl font-bold md:text-4xl">{t("มีไลน์ที่อยากให้เร็วขึ้น?", "Got a line that should run faster?")}</h2>
            <p className="mt-3 text-lg text-steel-200">{t("ปรึกษาฟรี เล่าโจทย์ให้เราฟัง แล้วเราจะเสนอแนวทางและราคาเบื้องต้น", "Consultation is free. Tell us the problem and we will come back with an approach and a first price.")}</p>
          </div>
          <div className="flex flex-wrap gap-3 md:justify-end">
            <a href={`tel:${company.tel}`} className="btn-primary">{company.phone}</a>
            <Link to="/contact" className="btn-ghost">{t("ส่งรายละเอียด", "Send details")}</Link>
          </div>
        </div>
      </section>
    </>
  );
}

export default Homepage;
